import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { DeviceMobile, X } from "@phosphor-icons/react";
import { useSavedLocations } from "../lib/SavedLocationsContext";
import { useAuth } from "../lib/AuthContext";

export const WELCOME_VIDEO_KEY = "firewatch:welcome_video_seen";

export default function WelcomeVideoModal() {
  const { locations } = useSavedLocations();
  const { user } = useAuth();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(WELCOME_VIDEO_KEY)) return;
    if (window.innerWidth >= 768 || window.matchMedia?.("(display-mode: standalone)").matches) return;
    if (user || locations.length > 0) {
      localStorage.setItem(WELCOME_VIDEO_KEY, "1");
      return;
    }
    const t = setTimeout(() => {
      localStorage.setItem(WELCOME_VIDEO_KEY, "1");
      localStorage.setItem("firewatch:tour_prompt_seen", "1");
      setShow(true);
    }, 1800);
    return () => clearTimeout(t);
  }, [user, locations.length]);

  if (!show) return null;
  const close = () => setShow(false);

  return createPortal(
    <div
      className="fixed inset-0 z-[2100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-3"
      onClick={close}
      data-testid="welcome-video-modal"
    >
      <div
        className="w-full max-w-sm bg-tac-bg border border-tac-primary/50 rounded-sm shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 px-4 py-3 border-b border-tac-border">
          <DeviceMobile size={20} weight="fill" className="text-tac-primary shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-primary">New here?</div>
            <div className="font-display font-black text-base text-tac-text leading-tight">
              Two minutes to learn the phone layout
            </div>
            <div className="text-[12px] text-tac-dim mt-0.5">
              Map / Feed switch, the Fires · DOT · More tabs, and how to install Firewatch on your home screen.
            </div>
          </div>
          <button data-testid="welcome-video-close" onClick={close} className="text-tac-faint hover:text-tac-text p-1 -m-1" aria-label="Skip">
            <X size={16} weight="bold" />
          </button>
        </div>
        <video
          data-testid="welcome-video"
          className="w-full bg-black aspect-[9/19.5] max-h-[58vh] object-contain"
          controls
          playsInline
          preload="metadata"
          poster="/docs/firewatch_tutorial_mobile_poster.jpg"
        >
          <source src="/docs/firewatch_tutorial_mobile.mp4" type="video/mp4" />
          <track kind="captions" srcLang="en" label="English" src="/docs/firewatch_tutorial_mobile.vtt" default />
        </video>
        <div className="flex items-center gap-2 px-4 py-3 border-t border-tac-border">
          <button
            data-testid="welcome-video-skip"
            onClick={close}
            className="flex-1 py-2 text-[11px] font-bold uppercase tracking-wider border border-tac-border bg-tac-surface text-tac-text rounded-sm hover:bg-tac-surface2"
          >
            Skip
          </button>
          <Link
            to="/guide"
            onClick={close}
            data-testid="welcome-video-guide"
            className="flex-1 py-2 text-center text-[11px] font-bold uppercase tracking-wider bg-tac-primary text-black rounded-sm hover:opacity-90"
          >
            Full guide
          </Link>
        </div>
      </div>
    </div>,
    document.body,
  );
}
